import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import Avatar from '@mui/material/Avatar';
import BookmarkRounded from '@mui/icons-material/BookmarkRounded';
import CheckBoxRounded from '@mui/icons-material/CheckBoxRounded';
import BugReportRounded from '@mui/icons-material/BugReportRounded';
import BoltRounded from '@mui/icons-material/BoltRounded';
import KeyboardDoubleArrowUpRounded from '@mui/icons-material/KeyboardDoubleArrowUpRounded';
import KeyboardArrowUpRounded from '@mui/icons-material/KeyboardArrowUpRounded';
import DragHandleRounded from '@mui/icons-material/DragHandleRounded';
import KeyboardArrowDownRounded from '@mui/icons-material/KeyboardArrowDownRounded';

export const STATUSES = ['TODO', 'IN_PROGRESS', 'IN_REVIEW', 'DONE'];

export const STATUS_META = {
  TODO: { label: 'To Do', color: '#5f6b7a', bg: '#eef1f5' },
  IN_PROGRESS: { label: 'In Progress', color: '#123871', bg: '#e3eaf6' },
  IN_REVIEW: { label: 'In Review', color: '#7a4b00', bg: '#fdf1d6' },
  DONE: { label: 'Done', color: '#067647', bg: '#e3f6ea' },
};

export const TYPE_META = {
  FEATURE: { label: 'Feature', color: '#00767a', Icon: BookmarkRounded },
  SUBTASK: { label: 'Sub-task', color: '#3a5a8f', Icon: CheckBoxRounded },
  BUG: { label: 'Bug', color: '#b42318', Icon: BugReportRounded },
  EPIC: { label: 'Epic', color: '#6941c6', Icon: BoltRounded },
};

export const PRIORITY_META = {
  URGENT: { label: 'Urgent', color: '#b42318', Icon: KeyboardDoubleArrowUpRounded },
  HIGH: { label: 'High', color: '#c4320a', Icon: KeyboardArrowUpRounded },
  MEDIUM: { label: 'Medium', color: '#b58105', Icon: DragHandleRounded },
  LOW: { label: 'Low', color: '#3a5a8f', Icon: KeyboardArrowDownRounded },
};

export const PRIORITIES = ['URGENT', 'HIGH', 'MEDIUM', 'LOW'];
/** Types that live on boards and in sprints. Epics are planned on the backlog and roadmap. */
export const TYPES = ['FEATURE', 'SUBTASK', 'BUG'];
/** Every type, for create dialogs and search filters. */
export const ALL_TYPES = ['EPIC', 'FEATURE', 'SUBTASK', 'BUG'];
export const SIZES = ['S', 'M', 'L'];

export function TypeIcon({ type, size = 18 }) {
  const m = TYPE_META[type] || TYPE_META.FEATURE;
  return (
    <Tooltip title={m.label}>
      <Box component="span" sx={{ display: 'inline-flex', color: m.color }} aria-label={m.label}>
        <m.Icon sx={{ fontSize: size }} />
      </Box>
    </Tooltip>
  );
}

export function PriorityIcon({ priority, size = 18 }) {
  const m = PRIORITY_META[priority] || PRIORITY_META.MEDIUM;
  return (
    <Tooltip title={`${m.label} priority`}>
      <Box component="span" sx={{ display: 'inline-flex', color: m.color }} aria-label={`${m.label} priority`}>
        <m.Icon sx={{ fontSize: size }} />
      </Box>
    </Tooltip>
  );
}

export function StatusPill({ status, small }) {
  const m = STATUS_META[status] || STATUS_META.TODO;
  return (
    <Box component="span" sx={{
      display: 'inline-block', px: small ? 0.75 : 1, py: small ? 0.1 : 0.25, borderRadius: 1,
      fontSize: small ? 10.5 : 11.5, fontWeight: 700, letterSpacing: '0.03em', textTransform: 'uppercase',
      color: m.color, bgcolor: m.bg, whiteSpace: 'nowrap',
    }}>
      {m.label}
    </Box>
  );
}

export function PersonAvatar({ person, size = 24 }) {
  if (!person) {
    return (
      <Tooltip title="Unassigned">
        <Avatar sx={{ width: size, height: size, fontSize: size * 0.42, bgcolor: '#e2e7ee', color: '#5f6b7a' }}>?</Avatar>
      </Tooltip>
    );
  }
  return (
    <Tooltip title={person.name}>
      <Avatar sx={{ width: size, height: size, fontSize: size * 0.42, fontWeight: 700, bgcolor: colorFor(person.id) }}>
        {person.initials}
      </Avatar>
    </Tooltip>
  );
}

const AVATAR_COLORS = ['#123871', '#00767a', '#6941c6', '#b54708', '#3a5a8f', '#067647', '#c11574', '#5f6b7a'];
export function colorFor(id) {
  return AVATAR_COLORS[Math.abs(Number(id) || 0) % AVATAR_COLORS.length];
}

/** 45 -> "<1m", 2700 -> "45m", 12000 -> "3h 20m", 190000 -> "2d 4h". */
export function fmtDuration(seconds) {
  const s = Math.max(0, Number(seconds) || 0);
  if (s < 60) return '<1m';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h${m % 60 ? ` ${m % 60}m` : ''}`;
  const d = Math.floor(h / 24);
  return `${d}d${h % 24 ? ` ${h % 24}h` : ''}`;
}

/** Only In Progress / In Review age is worth flagging - To Do can wait. */
export function isStale(card) {
  return (card.status === 'IN_PROGRESS' || card.status === 'IN_REVIEW') && card.secondsInStatus > 3 * 86400;
}

export const fmtDateTime = (v) => (v ? String(v).replace('T', ' ').slice(0, 16) : '');

/** 0 -> "0m", 90 -> "1h 30m", 480 -> "8h", 2400 -> "1w" (5-day weeks, 8-hour days). */
export function fmtMinutes(min) {
  if (min == null) return '';
  let m = Math.max(0, Math.round(Number(min) || 0));
  const parts = [];
  const w = Math.floor(m / 2400); m -= w * 2400;
  const d = Math.floor(m / 480); m -= d * 480;
  const h = Math.floor(m / 60); m -= h * 60;
  if (w) parts.push(`${w}w`);
  if (d) parts.push(`${d}d`);
  if (h) parts.push(`${h}h`);
  if (m || !parts.length) parts.push(`${m}m`);
  return parts.join(' ');
}

/** 3 -> "3", 0.5 -> "0.5", null -> "". */
export const fmtPoints = (p) => (p == null ? '' : String(Number(p)));

/** Shareable deep link to a ticket inside the ERP. */
export const ticketHref = (key) => `#/work/${key}`;
